import { getCorporateUsers } from "../../../../app/api/corporate";
import { BaseAction } from "../../../../core/actions/BaseAction";
import {
    getRecoilStateValue,
    updateRecoilState,
} from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse } from "../../../../Orchestrator/Types/Index";
import {
    ICorporateUser,
    ICorporateUsersPage,
} from "../../../../Orchestrator/Types/Interface/ICorporate";
import { toCorporateUsers } from "../../../../Components/MyCompanyAccount/corporateView";
import { IUserResponse } from "../../../../Orchestrator/Types/Interface/IUser";
import { ICorporateResponse } from "../../../../Orchestrator/Types/Interface/ICorporate";

/** Loads the company's members for the Manage Employees panel. */
class GetCorporateUsersAction extends BaseAction<ICorporateUser[], void> {
    protected override deduplicate = true;

    override async run(): Promise<ICorporateUser[] | null> {
        const userData = getRecoilStateValue<IUserResponse>("userDataAtom");
        const corporateId = userData?.corporateId;
        if (corporateId == null) return null;

        updateRecoilState("corporateUsersLoadErrorAtom", false);

        try {
            const response: IApiResponse<ICorporateUsersPage> =
                await getCorporateUsers(corporateId);

            if (response.status === "ok" && response.answer) {
                // Rows nest the member details under `data`; flatten them or
                // the panel lists blanks.
                const corporate =
                    getRecoilStateValue<ICorporateResponse>("corporateDataAtom");
                const members = toCorporateUsers(response.answer, corporate?.ownerId);
                updateRecoilState("corporateUsersAtom", members);
                // The request is capped at the contract's max page size, so the
                // loaded count is not necessarily the roster size.
                updateRecoilState(
                    "corporateUsersTotalAtom",
                    Number(response.answer.page?.totalElements ?? members.length)
                );
                return members;
            }

            // Without this the panel cannot tell "no employees" from "the
            // request was refused" — and a 403 is the expected state until the
            // corporate read permissions are granted.
            console.warn("[GetCorporateUsersAction] Failed to fetch company members.");
            updateRecoilState("corporateUsersLoadErrorAtom", true);
            // Otherwise the error state never shows: the table keeps rendering
            // the previous roster.
            updateRecoilState("corporateUsersAtom", []);
            // Clear the total too: it feeds the "N of M loaded" hint, which
            // would otherwise render above an empty error state.
            updateRecoilState("corporateUsersTotalAtom", 0);
            return null;
        } catch (error: any) {
            console.error("[GetCorporateUsersAction] Error fetching members:", error);
            updateRecoilState("corporateUsersLoadErrorAtom", true);
            // Same as the non-ok branch: stale rows under an error flag mean the
            // error state never renders and the table lies.
            updateRecoilState("corporateUsersAtom", []);
            // Clear the total too: it feeds the "N of M loaded" hint, which
            // would otherwise render above an empty error state.
            updateRecoilState("corporateUsersTotalAtom", 0);
            return null;
        }
    }
}

export const getCorporateUsersAction = new GetCorporateUsersAction();
